import React, { memo, useMemo, useState } from 'react';
import { message, Modal, Tooltip } from 'antd';
import classNames from 'classnames';
import { useDispatch } from 'react-redux';
import { ApprovalWorkflowItemDto, AWStageDto } from '@api/Api';
import { useAuth } from '@hooks/useAuth';
import { MixpanelEventType, MixpanelService } from '@services/mixpanelService';
import {
  useRestartApprovalWorkflow,
  useSendReminderMemberApprovalStage,
  useUpdateApprovalWorkflow
} from '@hooks/queries/approval';
import { toggleApprovalModal } from '@redux/actions/modalAction';
import {
  CrossIcon,
  EditApprovalsIcon,
  PauseApprovalsIcon,
  RestoreAssetIcon,
  ResumeApprovalsIcon,
  SendIcon,
  SuccessIcon
} from '@icons';
import './AssetModalActions.less';

const RestartApprovalModal = React.lazy(
  () => import('@components/Modals/ApprovalModal/RestartApprovalModal')
);

type AssetModalActionsProps = {
  approvalWorkflow: ApprovalWorkflowItemDto;
  currentStage: AWStageDto | null;
  callback?: (aw: ApprovalWorkflowItemDto | null) => void;
  onCloseModal: () => void;
  onUpdateApprovalWorkflow: (aw: ApprovalWorkflowItemDto) => void;
};

export default memo(function AssetModalActions({
  approvalWorkflow,
  currentStage,
  callback,
  onCloseModal,
  onUpdateApprovalWorkflow
}: AssetModalActionsProps) {
  const { user } = useAuth(true);
  const dispatch = useDispatch();
  const [isOpenRestartConfirmation, setIsOpenRestartConfirmation] =
    useState(false);

  const {
    mutateAsync: updateApprovalWorkflow,
    isPending: isLoadingUpdateApprovalWorkflow
  } = useUpdateApprovalWorkflow({});

  const {
    mutateAsync: restartApprovalWorkflow,
    isPending: isLoadingRestartApprovalWorkflow
  } = useRestartApprovalWorkflow({});

  const {
    mutateAsync: sendReminderMemberApprovalStage,
    isPending: isLoadingSendReminderMemberApprovalStage
  } = useSendReminderMemberApprovalStage({});

  const isLoading =
    isLoadingUpdateApprovalWorkflow ||
    isLoadingRestartApprovalWorkflow ||
    isLoadingSendReminderMemberApprovalStage;

  const sendMixpanelEvent = () => {
    MixpanelService.track(user.id, MixpanelEventType.MANAGE_ASSETS, {
      addToFavorite: false,
      deleteAsset: false,
      newAsset: false,
      externalLinkShare: false,
      approvalRouting: true,
      newCommentAsset: false,
      annotationLeft: false,
      taskFromComment: false
    });
  };

  const reminderMembers = useMemo(() => {
    if (!currentStage?.members?.length) return [];
    return currentStage.members.filter((member) => {
      if (member.me) return false;
      if (!member.canSubmitDecision) return false;
      return (
        !member.decisions?.length ||
        !approvalWorkflow.assets.every((asset) =>
          member.decisions?.some(
            (decision) =>
              decision.assetVersionId === asset.versionId && !!decision.decision
          )
        )
      );
    });
  }, [currentStage?.members, approvalWorkflow.assets]);

  const onPauseResume = async (isPaused: boolean) => {
    if (isLoading || approvalWorkflow.isPaused === isPaused) return;
    try {
      const {
        data: { approvalWorkflow: resApprovalWorkflow }
      } = await updateApprovalWorkflow({
        id: approvalWorkflow.id,
        isPaused
      });
      sendMixpanelEvent();
      onUpdateApprovalWorkflow(resApprovalWorkflow);
      callback?.(resApprovalWorkflow);
      message.success({
        key: 'aw-pause-changed',
        content: isPaused
          ? `This approval routing is now paused.`
          : `${approvalWorkflow.name} approval routing is now resumed. Reviewers have been notified.`,
        className: 'message-dark-modal',
        icon: <SuccessIcon className="anticon" />,
        duration: 2
      });
    } catch (err: any) {
      message.error(err?.response?.data?.message || 'Something went wrong');
    }
  };

  const onRestart = async () => {
    if (isLoading) return;
    if (!approvalWorkflow?.id) return;
    try {
      const {
        data: { approvalWorkflow: resApprovalWorkflow }
      } = await restartApprovalWorkflow({
        id: approvalWorkflow.id
      });
      sendMixpanelEvent();
      setIsOpenRestartConfirmation(false);
      onUpdateApprovalWorkflow(resApprovalWorkflow);
      callback?.(resApprovalWorkflow);
      message.success({
        key: 'aw-restarted',
        content: `${approvalWorkflow.name} approval routing has been restarted from the beginning.`,
        className: 'message-dark-modal',
        icon: <SuccessIcon className="anticon" />,
        duration: 2
      });
    } catch (err: any) {
      message.error(err?.response?.data?.message || 'Something went wrong');
    }
  };

  const onEdit = () => {
    if (!approvalWorkflow) return;
    onCloseModal();
    dispatch(
      toggleApprovalModal({
        visible: true,
        permissions: {
          create: true
        },
        campaignId: approvalWorkflow.campaignId,
        approvalWorkflowId: approvalWorkflow.id,
        callback
      })
    );
  };

  const onSendReminders = async () => {
    if (!currentStage?.id) return;
    if (isLoading) return;
    Modal.confirm({
      onOk: async () => {
        await sendReminderMemberApprovalStage({
          id: approvalWorkflow.id,
          stageId: currentStage.id,
          emails: undefined
        });

        message.success({
          content: 'Reminders are successfully sent',
          className: 'message-dark-modal b-with-button',
          icon: <SuccessIcon className="anticon" />
        });
      },
      okButtonProps: {
        style: { minWidth: 147, height: 48, padding: '8px 15px' }
      },
      cancelButtonProps: {
        style: {
          marginRight: '16px',
          border: 'none',
          height: 48,
          padding: '8px 32px'
        }
      },
      width: 464,
      icon: null,
      centered: true,
      okText: 'Send',
      cancelText: 'Cancel',
      closable: true,
      maskClosable: true,
      closeIcon: <CrossIcon />,
      content: (
        <div className="modal_container">
          <h2
            className="modal_container__title"
            style={{
              fontSize: 22,
              marginBottom: 16,
              gap: 8,
              alignItems: 'center'
            }}
          >
            <SendIcon />
            Send reminders
          </h2>
          <p className="main-body-text">
            This will notify all reviewers{' '}
            <strong>({reminderMembers.length})</strong> who have not yet
            submitted their decision.
          </p>
        </div>
      )
    });
  };

  return (
    <div className="asset-modal-actions">
      <Tooltip
        overlayClassName="toggle_favorite_overlay"
        title="Clear all approval history for all media and start from scratch."
        trigger="hover"
        placement="bottomRight"
      >
        <div
          className={classNames(
            'asset-modal-actions_item',
            isLoading && 'asset-modal-actions_item-loading'
          )}
          onClick={() => {
            setIsOpenRestartConfirmation(true);
          }}
        >
          <RestoreAssetIcon className="asset-modal-actions_icon" />
          <span>Restart</span>
        </div>
      </Tooltip>
      <Tooltip
        overlayClassName="toggle_favorite_overlay"
        title={
          approvalWorkflow.isPaused
            ? 'Clear all approval history for all media and start from scratch.'
            : 'Pause approval routing for all media at any time.'
        }
        trigger="hover"
        placement="bottomRight"
      >
        <div
          className={classNames(
            'asset-modal-actions_item',
            isLoading && 'asset-modal-actions_item-loading'
          )}
          onClick={() => {
            onPauseResume(!approvalWorkflow.isPaused);
          }}
        >
          {approvalWorkflow.isPaused ? (
            <ResumeApprovalsIcon className="asset-modal-actions_icon" />
          ) : (
            <PauseApprovalsIcon className="asset-modal-actions_icon" />
          )}
          <span>{approvalWorkflow.isPaused ? 'Resume' : 'Pause'}</span>
        </div>
      </Tooltip>

      <Tooltip
        overlayClassName="toggle_favorite_overlay"
        title="Any edits will be applied to all media in the batch approval."
        trigger="hover"
        placement="bottomRight"
      >
        <div
          className={classNames(
            'asset-modal-actions_item',
            isLoading && 'asset-modal-actions_item-loading'
          )}
          onClick={onEdit}
        >
          <EditApprovalsIcon className="asset-modal-actions_icon" />
          <span>Edit</span>
        </div>
      </Tooltip>

      {!approvalWorkflow.isFinished &&
        !approvalWorkflow.isPaused &&
        !!reminderMembers.length && (
          <div
            className={classNames(
              'asset-modal-actions_item',
              isLoading && 'asset-modal-actions_item-loading'
            )}
            onClick={onSendReminders}
          >
            <SendIcon className="asset-modal-actions_icon" />
            <span>Send reminders</span>
          </div>
        )}

      <RestartApprovalModal
        approvalWorkflowName={approvalWorkflow.name || ''}
        visible={isOpenRestartConfirmation}
        onCancel={() => setIsOpenRestartConfirmation(false)}
        onConfirm={onRestart}
      />
    </div>
  );
});
